import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const EditableTextSection = ({ apiKey, title }) => {
  const { t } = useTranslation();

  const [application, setApplication] = useState("");
  const [text, setText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" }); // ✅ type: "error" | "success"
  const [loading, setLoading] = useState(false);

  // Récupère l'application depuis le localStorage
  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication");
    if (storedApp) setApplication(storedApp);
  }, []);

  // Récupère le texte quand application connue
  useEffect(() => {
    if (application && application.trim() !== "") fetchText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, apiKey]);

  const fetchText = async () => {
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/${apiKey}`, {
        params: { application },
      });

      setText(res.data?.[apiKey] ?? "");
    } catch (err) {
      const raw = err?.response?.data?.message || err?.message || "";
      const fallback = t("editable_load_failed", { title });
      setMsg({ type: "error", text: raw ? `${fallback} ${raw}` : fallback });
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setEditedText(text);
    setMsg({ type: "", text: "" });
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditedText("");
    setMsg({ type: "", text: "" });
  };

  const handleSave = async () => {
    if (!editedText.trim()) {
      setMsg({ type: "error", text: t("editable_text_empty") });
      return;
    }

    setLoading(true);
    setMsg({ type: "", text: "" });

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/${apiKey}`,
        { [apiKey]: editedText },
        { params: { application } }
      );

      setText(editedText);
      setEditMode(false);
      setMsg({ type: "success", text: t("editable_update_success", { title }) });
    } catch (err) {
      const raw = err?.response?.data?.message || err?.message || "";
      const fallback = t("editable_update_failed");
      setMsg({ type: "error", text: raw ? `${fallback} ${raw}` : fallback });
    } finally {
      setLoading(false);
    }
  };

  const messageClass =
    msg.type === "error"
      ? "mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mb-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>

        {editMode ? (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
              disabled={loading}
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {loading ? t("saving") : t("save")}
            </button>
          </div>
        ) : (
          <button
            onClick={handleEdit}
            className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
          >
            {t("edit")}
          </button>
        )}
      </div>

      {msg.text && <p className={messageClass}>{msg.text}</p>}

      {editMode ? (
        <div className="mt-4">
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={6}
            className={inputClass}
            placeholder={t("editable_placeholder", { title })}
          />
        </div>
      ) : (
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{text}</p>
      )}
    </div>
  );
};

export default EditableTextSection;
